import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Where the reader sits next to the list, or whether it opens on its own. */
export type ReadingPane = 'right' | 'bottom' | 'off';
/** How tight the rows are; the design tokens define the three steps. */
export type Density = 'compact' | 'default' | 'comfortable';

/** Whether a folder is read as threads or as single messages. */
export type MailView = 'conversation' | 'message';

/** How much room the folder column takes, or whether it is there at all. */
export type SidebarMode = 'expanded' | 'icon' | 'offcanvas';
/** How the folder column is drawn against the rest of the window. */
export type SidebarVariant = 'sidebar' | 'inset' | 'floating';
/** Whether the window fills the screen or sits in a column of its own. */
export type ContentLayout = 'full' | 'centered';

export interface LayoutState {
  readingPane: ReadingPane;
  density: Density;
  /**
   * The view each folder was last read in. Threads suit the inbox, single
   * messages suit Sent and Drafts, and people differ — so the choice is
   * remembered per folder instead of globally (GAP B4).
   */
  viewByFolder: Record<string, MailView>;
  /** Width of the folder pane and of the list pane, in percent of the window. */
  folderPaneSize: number;
  listPaneSize: number;
  sidebarMode: SidebarMode;
  sidebarVariant: SidebarVariant;
  contentLayout: ContentLayout;
  /** Declared as properties, not methods: they are handed to components as values. */
  setReadingPane: (pane: ReadingPane) => void;
  setDensity: (density: Density) => void;
  setPaneSizes: (sizes: { folder?: number; list?: number }) => void;
  rememberFolderView: (folderId: string, view: MailView) => void;
  setSidebarMode: (mode: SidebarMode) => void;
  setSidebarVariant: (variant: SidebarVariant) => void;
  setContentLayout: (layout: ContentLayout) => void;
}

const DEFAULTS = {
  readingPane: 'right',
  density: 'default',
  folderPaneSize: 18,
  listPaneSize: 34,
  viewByFolder: {},
  sidebarMode: 'expanded',
  // The folders on the page itself and the mail on panes lifted off it: the look of the product.
  sidebarVariant: 'inset',
  contentLayout: 'full',
} as const;

/** What `Reset to default` puts back; the pane sizes are the window's, not the theme's. */
export const LAYOUT_DEFAULTS = DEFAULTS;

/**
 * What the window looks like, remembered per browser. It is UI state, not
 * server state, so it lives in a store rather than in the query cache
 * (ADR 0006); the mail the panes show comes from the URL.
 */
export const useLayout = create<LayoutState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setReadingPane: (readingPane) => set({ readingPane }),
      setDensity: (density) => set({ density }),
      setPaneSizes: ({ folder, list }) =>
        set((state) => ({
          folderPaneSize: folder ?? state.folderPaneSize,
          listPaneSize: list ?? state.listPaneSize,
        })),
      rememberFolderView: (folderId, view) =>
        set((state) => ({ viewByFolder: { ...state.viewByFolder, [folderId]: view } })),
      setSidebarMode: (sidebarMode) => set({ sidebarMode }),
      setSidebarVariant: (sidebarVariant) => set({ sidebarVariant }),
      setContentLayout: (contentLayout) => set({ contentLayout }),
    }),
    {
      name: 'web.layout',
      version: 4,
      /*
       * Version 4 is the redesigned look, whose panes are lifted off the page. A
       * window still on the old default column takes the new one; one somebody
       * had set to `floating` keeps it.
       */
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<LayoutState>;
        if (version < 4 && state.sidebarVariant === 'sidebar') return { ...state, sidebarVariant: 'inset' };
        return state;
      },
    },
  ),
);
